/**
 * All phone numbers must:
 * - be 10 digits.
 * - start with 02, 03, 04, 07 or 08 (ie include the area code).
 * - More info: https://www.australia.gov.au/telephone-country-and-area-codes
 * @param phone - in local format, eg 0466 338 201
 */
export const convertToIntFormat = (localPhone: string): string => {
  // Note: isMobileNumberValid() should be run first, before passing value the value this function.
  // This clause is added as a safety catch.
  if (!isMobileNumberValid(localPhone)) {
    throw new Error(`Local phone number is not a valid format. ${localPhone}`);
  }
  return `+614${localPhone.substr(2)}`;
};

/**
 * Convert the international format to local format.
 * Useful when loading data from DB to UI forms.
 * @param intPhone - eg "+61433928383"
 * @returns localPhone - eg "0433928383" - TODO: Depreciate - use formatMobile() instead.
 */
export const convertToLocalFormat = (intPhone: string): string => {
  if (!intPhone) return '';
  if (intPhone.substr(0, 3) === '+61' && intPhone.length === 12) {
    const localFormat = `0${intPhone.substr(3)}`;

    return `${localFormat.substr(0, 4)} ${localFormat.substr(4, 3)} ${localFormat.substr(7, 3)}`;
  } else {
    return intPhone;
  }
};

/**
 * Returns mobile number in a formatted local format: '04xx xxx xxx'.
 * @param intMobile
 */
export function formatMobile(intMobile: string): string {
  // Convert international format to local format
  const mobileNumber = `0${intMobile.slice(3)}`;

  if (mobileNumber.length !== 10) {
    return 'INVALID_MOBILE_NUMBER';
  }

  return `${mobileNumber.slice(0, 4)} ${mobileNumber.slice(4, 7)} ${mobileNumber.slice(7, 10)}`;
}

/**
 * Checks if a mobile phone number is a valid format.
 * Empty spaces should be removed before passing in the string to this function. eg "0433 442 393".
 * @example "0433442393" (mobile)
 * @param phone - Mobile phone number
 */
export const isMobileNumberValid = (phone: string): boolean => {
  // Check it's not empty
  if (!phone) return false;

  // Check it is digits only
  if (!onlyNumbers(phone)) return false;

  // Check it's 10 digits long
  if (phone.length !== 10) return false;

  // Ensure is has a valid area code
  // Only allow Australian mobile numbers for now (ie '04' prefix).
  const validAreaCodes = ['04'];
  const areaCode = phone.substring(0, 2);
  if (!validAreaCodes.includes(areaCode)) return false;

  return true;
};

/**
 * Checks if a local phone number is a valid format.
 * @example "0352418293" (local phone)
 * @param phone - Local phone number
 */
export const isLandlineNumberValid = (phone: string): boolean => {
  // Check it's not empty
  if (!phone) return false;

  // Check it is digits only
  if (!onlyNumbers(phone)) return false;

  // Check it's 10 digits long
  if (phone.length !== 10) return false;

  // Ensure is has a valid area code
  // '04' is included because a mobile should also be a valid landline.
  // More info: https://www.australia.gov.au/telephone-country-and-area-codes
  const validAreaCodes = ['02', '03', '04', '07', '08'];
  const areaCode = phone.substring(0, 2);
  if (!validAreaCodes.includes(areaCode)) return false;

  return true;
};

export function onlyNumbers(str: string) {
  return /^[0-9]+$/.test(str);
}
